import { ApiService } from './ApiService';

export interface UserModel {
	id: number;
	firstName?: string;
	lastName?: string;
	email?: string;
	workdUserId?: number;
	isActive: boolean;
}

/**
 * The calling rep's own lead-eligibility snapshot (PD-13595) — drives the
 * Assign to Me button state on lead detail pages.
 */
export interface MyLeadEligibilityModel {
	id: number;
	isDistributionEligible: boolean;
	currentLeads: number;
	maxLeads: number | null;
}

interface UsersApiResponse {
	data?: Array<UserModel>;
	users?: Array<UserModel>;
	success?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class UsersQueryService {
	public static async getUsers(searchTerm?: string): Promise<Array<UserModel>> {
		try {
			const params: Record<string, string> = {};

			if (searchTerm != null) {
				params.searchTerm = searchTerm;
			}

			const response = await ApiService.apiGet<
				UsersApiResponse | Array<UserModel>
			>('/users', Object.keys(params).length > 0 ? params : undefined);

			if (Array.isArray(response)) {
				return response;
			}

			if (response.data != null) {
				return response.data;
			}

			if (response.users != null) {
				return response.users;
			}

			return [];
		} catch (error) {
			console.error('Error fetching users:', error);

			return [];
		}
	}

	/**
	 * The calling user's own lead-eligibility settings (cap + usage). Self-scope
	 * endpoint — works for reps without the user-management permission.
	 */
	public static async getMyLeadEligibility(): Promise<MyLeadEligibilityModel | null> {
		try {
			const response = await ApiService.apiGet<{
				data?: MyLeadEligibilityModel;
				success?: boolean;
			} | null>('/users/lead-eligibility/me');

			return response?.data ?? null;
		} catch (error) {
			console.error('Error fetching my lead eligibility:', error);

			return null;
		}
	}

	/**
	 * Get user by instance user ID (tenant-specific ID stored in _id column)
	 * Use this for: fkUserId fields, user selectors, tenant-specific lookups
	 */
	public static async getUserByInstanceId(
		instanceId: number,
	): Promise<{ data: UserModel | null; success: boolean; error?: string }> {
		try {
			const response = await ApiService.apiGet<UserModel>(
				`/users/by-instance/${instanceId}`,
			);

			return {
				data: response,
				success: true,
			};
		} catch (error) {
			console.error('Error fetching user by instance ID:', error);

			return {
				data: null,
				success: false,
				error: 'Failed to fetch user',
			};
		}
	}

	/**
	 * Get user by workd user ID (global ID stored in _workd__user__id column)
	 * Use this for: RBAC operations, cross-tenant lookups
	 */
	public static async getUserByWorkdId(
		workdId: number,
	): Promise<{ data: UserModel | null; success: boolean; error?: string }> {
		try {
			const response = await ApiService.apiGet<UserModel>(
				`/users/by-workd/${workdId}`,
			);

			return {
				data: response,
				success: true,
			};
		} catch (error) {
			console.error('Error fetching user by workd ID:', error);

			return {
				data: null,
				success: false,
				error: 'Failed to fetch user',
			};
		}
	}

	public static async getSalespeople(
		searchTerm?: string,
		includeAll?: boolean,
	): Promise<Array<UserModel>> {
		try {
			const params: Record<string, string> = {};

			if (searchTerm != null) {
				params.searchTerm = searchTerm;
			}

			if (includeAll === true) {
				params.includeAll = 'true';
			}

			const response = await ApiService.apiGet<
				UsersApiResponse | Array<UserModel>
			>('/users/salespeople', params);

			if (Array.isArray(response)) {
				return response;
			}

			if (response.data != null) {
				return response.data;
			}

			return [];
		} catch (error) {
			console.error('Error fetching salespeople:', error);

			return [];
		}
	}
}
